import { FunctionComponent, useCallback, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import {
	faArchive,
	faBolt,
	faChevronUp,
	faCoins,
	faCircleExclamation,
	faClock,
	faDownload,
	faFile,
	faFileSlash,
	faFolderArrowDown,
	faPaperPlane,
	faTrash,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { CrmProvider } from "@/pages/Private/redux/crm/crm.schema";
import { useConnectedCrmProviders } from "@/pages/Private/pages/Integrations/hooks/useConnectedCrmProviders";

import { Icon } from "../Icon/Icon";

import { FooterActionItem } from "./FooterActionItem";
import { FooterBlacklistGroup } from "./FooterBlacklistGroup";
import { TableFooterProps } from "./table-footer.types";
import { FooterPopoverGroup } from "./FooterPopoverGroup";
import { FooterAddToGroup } from "./FooterAddToGroup";
import { FooterEnrichGroup } from "./FooterEnrichGroup";
import { useTableFooterPopovers } from "./useTableFooterPopovers";
import { TableFooterPaginationBar } from "./TableFooterPaginationBar";

export const TableFooter: FunctionComponent<TableFooterProps> = ({
	rowsPerPage,
	overallCount,
	columns,
	coulumsShown,
	handleExport,
	showFooterFunctions,
	handleArchive,
	handleDelete,
	currentlyDownloadingSelected,
	hideColumnsCount,
	exportIsLoading,
	addToProfileTracker,
	disqualify,
	archive,
	parked,
	unpark,
	addToBlacklist,
	removeFromBlacklist,
	unflagCompetitor,
	addToList,
	searchForEmail,
	addToWebhook,
	addToAnotherList,
	addToCrm,
	deleteOrganizations,
	extraActions,
	snooze,
	unsnooze,
	unarchive,
	unarchiveToReview,
	searchForPhone,
	searchForCompanyEmailAndPhone,
	leadScoring,
	hidePagination,
	hideFooter,
	currentPage,
	totalPages,
	handleNavigateToPage,
	handleChangeRowsPerPage,
	limit,
	selectedCount,
	clearSelection,
	deepResearch,
	bulkFindPerson,
	reachOut,
	enrichAnyway,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`basics.${key}`), [t]);
	const connectedCrmProviders = useConnectedCrmProviders();

	const {
		openPopover,
		setOpenPopover,
		togglePopover,
		showListPopover,
		setShowListPopover,
		popoverRef,
	} = useTableFooterPopovers();
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	const hasCrmItems = !!addToCrm && connectedCrmProviders.length > 0;

	const hasStatusActions = !!(disqualify || addToList || unpark || parked || archive);
	const hasEnrichActions = !!(
		searchForEmail ||
		searchForPhone ||
		searchForCompanyEmailAndPhone ||
		leadScoring ||
		deepResearch ||
		bulkFindPerson
	);
	const hasArchiveActions = !!(unarchive || unarchiveToReview || handleArchive);
	const hasSnoozeActions = !!(snooze || unsnooze);
	const hasBlacklistActions = !!(removeFromBlacklist || unflagCompetitor);
	const hasAddToActions = !!(
		addToProfileTracker ||
		addToWebhook ||
		addToAnotherList ||
		addToBlacklist ||
		hasCrmItems
	);

	const isStatusLoading = !!(
		disqualify?.disqualifyIsLoading ||
		addToList?.addToListIsLoading ||
		unpark?.unparkIsLoading ||
		parked?.parkedIsLoading ||
		archive?.archiveIsLoading
	);
	const isArchiveLoading = !!(
		unarchive?.unarchiveIsLoading || unarchiveToReview?.unarchiveToReviewIsLoading
	);
	const isSnoozeLoading = !!(snooze?.snoozeIsLoading || unsnooze?.unsnoozeIsLoading);

	const hasAnyAction = !!(
		enrichAnyway ||
		reachOut ||
		handleExport ||
		hasStatusActions ||
		hasEnrichActions ||
		hasArchiveActions ||
		hasSnoozeActions ||
		hasBlacklistActions ||
		hasAddToActions ||
		deleteOrganizations ||
		extraActions?.length ||
		handleDelete
	);

	const hasSelection = selectedCount === undefined ? true : selectedCount > 0;
	const showActionsPanel = !!showFooterFunctions && !hideFooter && hasAnyAction && hasSelection;
	const showPaginationPanel = !hidePagination;

	useEffect(() => {
		const handleResize = () => {
			const footerElement = document.querySelector("#table-footer") as HTMLElement;
			const floatingBarElement = document.getElementById("table-floating-bulk-bar");
			const outletElement = document.getElementById("outlet-container");

			if (outletElement && footerElement) {
				footerElement.style.setProperty("width", `${outletElement.offsetWidth}px`, "important");
			}

			if (outletElement && floatingBarElement) {
				const rect = outletElement.getBoundingClientRect();
				const center = rect.left + rect.width / 2;

				floatingBarElement.style.setProperty("left", `${center}px`, "important");
				floatingBarElement.style.setProperty("max-width", `${rect.width - 16}px`, "important");
			}
		};

		handleResize();

		const outletElement = document.getElementById("outlet-container");

		if (outletElement) {
			resizeObserverRef.current = new ResizeObserver(handleResize);
			resizeObserverRef.current.observe(outletElement);
		}

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			resizeObserverRef.current?.disconnect();
		};
	}, [showActionsPanel]);

	if (hideFooter && hidePagination) {
		return null;
	}

	return (
		<>
			{/* Floating bulk action bar — appears above the footer when items are selected */}
			{showActionsPanel && (
				<div
					className="fixed bottom-[54px] left-1/2 -translate-x-1/2 z-150 flex flex-row items-center gap-2 bg-gray-900 text-white rounded-lg pl-4 pr-2 py-2 shadow-lg"
					id="table-floating-bulk-bar"
				>
					{selectedCount !== undefined && (
						<span className="flex items-center gap-1.5 text-xs font-medium whitespace-nowrap">
							<span className="bg-blue-action text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
								{selectedCount}
							</span>
							selected
						</span>
					)}
					{clearSelection && (
						<button
							aria-label="Clear selection"
							className="flex items-center justify-center w-5 h-5 rounded text-white cursor-pointer hover:bg-white-semi"
							type="button"
							onClick={clearSelection}
						>
							<Icon className="w-3.5 h-3.5" icon={faXmark} />
						</button>
					)}
					{/* Recovery cost — shown when the selection contains title-excluded leads (PREC-3536). */}
					{showFooterFunctions && enrichAnyway && (
						<span
							className="flex items-center gap-1.5 text-xs font-medium whitespace-nowrap text-white"
							data-testid="footer-enrich-anyway-cost"
						>
							<Icon className="w-3.5 h-3.5" icon={faCoins} />
							{enrichAnyway.costLabel}
						</span>
					)}
					<span className="w-px h-5 bg-white-semi mx-1" />
					<div ref={popoverRef} className="flex flex-row gap-1 items-center">
						{/* Enrich anyway — recover the selected title-excluded leads (PREC-3536 Phase 5). */}
						{showFooterFunctions && enrichAnyway && (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer text-white bg-blue-action hover:opacity-90 transition-opacity"
								data-testid="footer-enrich-anyway"
								type="button"
								onClick={enrichAnyway.onOpen}
							>
								<Icon className="w-3.5 h-3.5" icon={faBolt} />
								<span className="whitespace-nowrap">{enrichAnyway.label}</span>
							</button>
						)}
						{/* Campaign — same dark trigger styling as the other categories; opens the reach-out
						    menu over the selection (owner renders it, anchored to this button). */}
						{showFooterFunctions && reachOut && (
							<button
								className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer text-white hover:bg-white-semi transition-colors"
								data-coachmark="reach-out-trigger"
								data-testid="footer-reach-out"
								type="button"
								onClick={(e) => reachOut.onOpen(e.currentTarget.getBoundingClientRect())}
							>
								<Icon className="w-3.5 h-3.5" icon={faPaperPlane} />
								<span className="whitespace-nowrap">{ts("campaign")}</span>
								<Icon className="w-2.5 h-2.5" icon={faChevronUp} />
							</button>
						)}

						{/* Standalone: Export */}
						{showFooterFunctions && handleExport && (
							<FooterActionItem
								dark
								icon={faDownload}
								isLoading={currentlyDownloadingSelected || exportIsLoading}
								label={ts("export")}
								loadingLabel={ts("wait")}
								onClick={() => handleExport()}
							/>
						)}

						{/* Group: Status */}
						{showFooterFunctions && hasStatusActions && (
							<FooterPopoverGroup
								dark
								icon={faCircleExclamation}
								isLoading={isStatusLoading}
								isOpen={openPopover === "status"}
								label={ts("status")}
								loadingLabel={ts("wait")}
								onToggle={() => togglePopover("status")}
							>
								{disqualify && (
									<FooterActionItem
										icon={faFileSlash}
										isMenuItem
										label={t("inbox.excludeFromList")}
										onClick={() => {
											disqualify?.disqualify?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{addToList && (
									<FooterActionItem
										icon={faFile}
										isMenuItem
										label="To Found"
										onClick={() => {
											addToList?.addToList?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{unpark && (
									<FooterActionItem
										icon={faFile}
										isMenuItem
										label={ts("revertToBeChecked")}
										onClick={() => {
											unpark?.unpark?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{parked && (
									<FooterActionItem
										icon={faClock}
										isMenuItem
										label="Review Later"
										onClick={() => {
											parked?.parked?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{archive && (
									<FooterActionItem
										icon={faArchive}
										isMenuItem
										label="Ignore"
										onClick={() => {
											archive?.archive?.();
											setOpenPopover(null);
										}}
									/>
								)}
							</FooterPopoverGroup>
						)}

						{/* Group: Enrich */}
						{showFooterFunctions && hasEnrichActions && (
							<FooterEnrichGroup
								bulkFindPerson={bulkFindPerson}
								deepResearch={deepResearch}
								isOpen={openPopover === "enrich"}
								leadScoring={leadScoring}
								searchForCompanyEmailAndPhone={searchForCompanyEmailAndPhone}
								searchForEmail={searchForEmail}
								searchForPhone={searchForPhone}
								ts={ts}
								onClose={() => setOpenPopover(null)}
								onToggle={() => togglePopover("enrich")}
							/>
						)}

						{/* Group: Archive */}
						{showFooterFunctions && hasArchiveActions && (
							<FooterPopoverGroup
								dark
								icon={faArchive}
								isLoading={isArchiveLoading}
								isOpen={openPopover === "archive"}
								label={ts("archive")}
								loadingLabel={ts("wait")}
								onToggle={() => togglePopover("archive")}
							>
								{unarchive && (
									<FooterActionItem
										icon={faArchive}
										isMenuItem
										label={ts("unarchiveToDraft")}
										onClick={() => {
											unarchive?.unarchive?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{unarchiveToReview && (
									<FooterActionItem
										icon={faArchive}
										isMenuItem
										label={ts("unarchiveToReview")}
										onClick={() => {
											unarchiveToReview?.unarchiveToReview?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{handleArchive && (
									<FooterActionItem
										icon={faFolderArrowDown}
										isMenuItem
										label={ts("archive")}
										onClick={() => {
											handleArchive();
											setOpenPopover(null);
										}}
									/>
								)}
							</FooterPopoverGroup>
						)}

						{/* Group: Snooze */}
						{showFooterFunctions && hasSnoozeActions && (
							<FooterPopoverGroup
								dark
								icon={faClock}
								isLoading={isSnoozeLoading}
								isOpen={openPopover === "snooze"}
								label="Snooze"
								loadingLabel={ts("wait")}
								onToggle={() => togglePopover("snooze")}
							>
								{snooze && (
									<FooterActionItem
										icon={faClock}
										isMenuItem
										label="Change Snooze"
										onClick={() => {
											snooze?.snooze?.();
											setOpenPopover(null);
										}}
									/>
								)}
								{unsnooze && (
									<FooterActionItem
										icon={faClock}
										isMenuItem
										label="Unsnooze"
										onClick={() => {
											unsnooze?.unsnooze?.();
											setOpenPopover(null);
										}}
									/>
								)}
							</FooterPopoverGroup>
						)}

						{showFooterFunctions && hasBlacklistActions && (
							<FooterBlacklistGroup
								isOpen={openPopover === "blacklist"}
								removeFromBlacklist={removeFromBlacklist}
								unflagCompetitor={unflagCompetitor}
								onAction={() => setOpenPopover(null)}
								onToggle={() => togglePopover("blacklist")}
							/>
						)}

						{/* Group: Add to */}
						{showFooterFunctions && hasAddToActions && (
							<FooterAddToGroup
								addToAnotherList={addToAnotherList}
								addToBlacklist={addToBlacklist}
								addToCrm={addToCrm}
								addToProfileTracker={addToProfileTracker}
								addToWebhook={addToWebhook}
								connectedCrmProviders={connectedCrmProviders}
								isOpen={openPopover === "addTo"}
								setShowListPopover={setShowListPopover}
								showListPopover={showListPopover}
								ts={ts}
								onClose={() => setOpenPopover(null)}
								onToggle={() => {
									togglePopover("addTo");
									setShowListPopover(false);
								}}
							/>
						)}

						{/* Standalone: Delete Organizations */}
						{showFooterFunctions && deleteOrganizations && (
							<FooterActionItem
								dark
								icon={faTrash}
								isLoading={deleteOrganizations.deleteOrganizationsIsLoading}
								label="Delete Organizations"
								loadingLabel={ts("wait")}
								onClick={() => deleteOrganizations?.deleteOrganizations?.()}
							/>
						)}

						{showFooterFunctions &&
							extraActions?.map((action) => (
								<FooterActionItem
									key={action.label}
									dark
									icon={action.icon}
									isLoading={action.isLoading}
									label={action.label}
									loadingLabel={ts("wait")}
									onClick={action.onClick}
								/>
							))}

						{/* Standalone: Delete */}
						{showFooterFunctions && handleDelete && (
							<FooterActionItem
								dark
								icon={faTrash}
								label={ts("delete")}
								onClick={() => handleDelete()}
							/>
						)}
					</div>
				</div>
			)}

			{/* Always-on pagination/export footer */}
			{showPaginationPanel && (
				<TableFooterPaginationBar
					columns={columns}
					coulumsShown={coulumsShown}
					currentPage={currentPage}
					currentlyDownloadingSelected={currentlyDownloadingSelected}
					exportIsLoading={exportIsLoading}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					handleExport={handleExport}
					handleNavigateToPage={handleNavigateToPage}
					hideColumnsCount={hideColumnsCount}
					hidePagination={hidePagination}
					limit={limit}
					overallCount={overallCount}
					rowsPerPage={rowsPerPage}
					totalPages={totalPages}
				/>
			)}
		</>
	);
};
